/* markers.svelte.ts generated by Svelte v5.55.4 */
import * as $ from 'svelte/internal/client';

import {
	toRegionCoords,
	toGlobalCoords,
	markerKey,
	packMarkers,
	unpackMarkers,
	compressToUrl,
	decompressFromUrl
} from "@/helpers";

class MarkerStore {
	#markers = $.state($.proxy([]));

	get markers() {
		return $.get(this.#markers);
	}

	set markers(value) {
		$.set(this.#markers, value, true);
	}

	#index = $.state($.proxy({}));

	get index() {
		return $.get(this.#index);
	}

	set index(value) {
		$.set(this.#index, value, true);
	}

	#undoStack = $.state($.proxy([]));

	get undoStack() {
		return $.get(this.#undoStack);
	}

	set undoStack(value) {
		$.set(this.#undoStack, value, true);
	}

	#redoStack = $.state($.proxy([]));

	get redoStack() {
		return $.get(this.#redoStack);
	}

	set redoStack(value) {
		$.set(this.#redoStack, value, true);
	}

	#pendingAction = $.state(null);

	get pendingAction() {
		return $.get(this.#pendingAction);
	}

	set pendingAction(value) {
		$.set(this.#pendingAction, value, true);
	}

	onPlace = null;
	onRemove = null;

	get count() {
		return this.markers.length;
	}

	has(plane, x, y) {
		return this.index[markerKey(plane, x, y)] !== undefined;
	}

	getAt(plane, x, y) {
		const idx = this.index[markerKey(plane, x, y)];

		return idx !== undefined ? this.markers[idx] : undefined;
	}

	beginAction() {
		this.pendingAction = { added: [], removed: [] };
	}

	commitAction() {
		if (this.pendingAction && (this.pendingAction.added.length > 0 || this.pendingAction.removed.length > 0)) {
			this.undoStack.push(this.pendingAction);
			this.redoStack = [];
		}

		this.pendingAction = null;
		this.scheduleSyncToUrl();
	}

	place(plane, x, y, color) {
		const key = markerKey(plane, x, y);
		const existingIdx = this.index[key];

		if (existingIdx !== undefined) {
			const old = this.markers[existingIdx];

			if (old.color === color) return;

			if (this.pendingAction) {
				this.pendingAction.removed.push({ plane, x, y, color: old.color, label: old.label });
			}

			this.onRemove?.(plane, x, y);
			this._rawRemove(key, existingIdx);
		}

		this._rawPlace(plane, x, y, color);
		this.onPlace?.(plane, x, y, color);

		if (this.pendingAction) {
			this.pendingAction.added.push({ plane, x, y, color });
		}
	}

	remove(plane, x, y) {
		const key = markerKey(plane, x, y);
		const idx = this.index[key];

		if (idx === undefined) return;

		const m = this.markers[idx];

		if (this.pendingAction) {
			this.pendingAction.removed.push({ plane, x, y, color: m.color, label: m.label });
		}

		this.onRemove?.(plane, x, y);
		this._rawRemove(key, idx);
	}

	_rawPlace(plane, x, y, color, label) {
		const key = markerKey(plane, x, y);

		if (this.index[key] !== undefined) return;

		const region = toRegionCoords(plane, x, y);

		this.index[key] = this.markers.length;

		this.markers.push({
			regionId: region.regionId,
			regionX: region.regionX,
			regionY: region.regionY,
			z: region.z,
			color,
			label
		});
	}

	_rawRemove(key, idx) {
		const last = this.markers.length - 1;

		if (idx !== last) {
			const moved = this.markers[last];

			this.markers[idx] = moved;

			const c = toGlobalCoords(moved.regionId, moved.regionX, moved.regionY);

			this.index[markerKey(moved.z, c.globalX, c.globalY)] = idx;
		}

		this.markers.pop();
		delete this.index[key];
	}

	undo() {
		if (this.undoStack.length === 0) return;

		const action = this.undoStack.pop();
		const reverse = { added: [], removed: [] };

		for (const t of action.added) {
			const key = markerKey(t.plane, t.x, t.y);
			const idx = this.index[key];

			if (idx === undefined) continue;

			const m = this.markers[idx];

			reverse.added.push({
				plane: t.plane,
				x: t.x,
				y: t.y,
				color: m.color,
				label: m.label
			});

			this.onRemove?.(t.plane, t.x, t.y);
			this._rawRemove(key, idx);
		}

		for (const t of action.removed) {
			reverse.removed.push({ ...t });
			this._rawPlace(t.plane, t.x, t.y, t.color, t.label);
			this.onPlace?.(t.plane, t.x, t.y, t.color);
		}

		this.redoStack.push(reverse);
		this.scheduleSyncToUrl();
	}

	redo() {
		if (this.redoStack.length === 0) return;

		const action = this.redoStack.pop();
		const reverse = { added: [], removed: [] };

		for (const t of action.removed) {
			const key = markerKey(t.plane, t.x, t.y);
			const idx = this.index[key];

			if (idx === undefined) continue;

			const m = this.markers[idx];

			reverse.removed.push({
				plane: t.plane,
				x: t.x,
				y: t.y,
				color: m.color,
				label: m.label
			});

			this.onRemove?.(t.plane, t.x, t.y);
			this._rawRemove(key, idx);
		}

		for (const t of action.added) {
			reverse.added.push({ ...t });
			this._rawPlace(t.plane, t.x, t.y, t.color, t.label);
			this.onPlace?.(t.plane, t.x, t.y, t.color);
		}

		this.undoStack.push(reverse);
		this.scheduleSyncToUrl();
	}

	clearAll() {
		for (const m of this.markers) {
			const c = toGlobalCoords(m.regionId, m.regionX, m.regionY);

			this.onRemove?.(m.z, c.globalX, c.globalY);
		}

		this.markers = [];
		this.index = {};
		this.undoStack = [];
		this.redoStack = [];
		this.syncToUrl();
	}

	_syncTimer = null;

	scheduleSyncToUrl() {
		if (this._syncTimer) clearTimeout(this._syncTimer);

		this._syncTimer = setTimeout(() => this.syncToUrl(), 300);
	}

	syncToUrl() {
		const url = new URL(window.location.href);

		url.searchParams.delete("s");

		if (this.markers.length === 0) {
			url.searchParams.delete("markers");
			history.replaceState(null, "", url);

			return;
		}

		const packed = packMarkers(this.markers);

		compressToUrl(packed).then((encoded) => {
			url.searchParams.set("markers", encoded);
			history.replaceState(null, "", url);
		});
	}

	loadFromUrl() {
		const url = new URL(window.location.href);
		const shortId = url.searchParams.get("s");

		if (shortId) {
			fetch(`/api/expand/${encodeURIComponent(shortId)}`).then((r) => r.ok ? r.json() : null).then((data) => {
				if (data?.markers) this._loadEncoded(data.markers);
			}).catch((e) => console.error("Failed to expand short URL:", e));

			return;
		}

		const encoded = url.searchParams.get("markers");

		if (!encoded) return;

		this._loadEncoded(encoded);
	}

	_loadEncoded(encoded) {
		decompressFromUrl(encoded).then((bytes) => {
			const data = unpackMarkers(bytes);

			for (const m of data) {
				const coords = toGlobalCoords(m.regionId, m.regionX, m.regionY);
				const key = markerKey(m.z, coords.globalX, coords.globalY);

				if (this.index[key] !== undefined) continue;

				this._rawPlace(m.z, coords.globalX, coords.globalY, m.color, m.label);
				this.onPlace?.(m.z, coords.globalX, coords.globalY, m.color);
			}
		}).catch((e) => {
			console.error("Failed to load markers from URL:", e);
		});
	}
}

export const markerStore = new MarkerStore();